import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';

/**
 * Hook for making API calls with React Query
 */
export function useApiQuery(key, url, options = {}) {
  return useQuery({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: async () => {
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
}

/**
 * Hook for making API mutations
 */
export function useApiMutation(mutationFn, options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data, variables, context) => {
      // Invalidate queries if specified
      if (options.invalidateKeys) {
        options.invalidateKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] });
        });
      }
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}

// Specific API hooks

/**
 * Hook for fetching sports
 */
export function useSports() {
  return useApiQuery('sports', '/sports', {
    staleTime: 1000 * 60 * 60, // 1 hour - sports rarely change
  });
}

/**
 * Hook for fetching onboarding status
 */
export function useOnboardingStatus() {
  return useApiQuery('onboarding', '/onboarding/status');
}

/**
 * Hook for fetching incomplete onboarding steps (for profile completion card)
 */
export function useIncompleteSteps() {
  return useApiQuery('incomplete-steps', '/onboarding/incomplete-steps', {
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook for updating onboarding step
 */
export function useUpdateOnboarding() {
  return useApiMutation(
    async ({ step, data }) => {
      const response = await api.put(`/onboarding/step/${step}`, data);
      return response.data;
    },
    {
      invalidateKeys: ['onboarding', 'user'],
    }
  );
}

/**
 * Hook for skipping an onboarding step
 */
export function useSkipOnboarding() {
  return useApiMutation(
    async (step) => {
      const response = await api.post(`/onboarding/skip/${step}`);
      return response.data;
    },
    {
      invalidateKeys: ['onboarding', 'user', 'incomplete-steps'],
    }
  );
}

/**
 * Hook for fetching check-ins
 */
export function useCheckins(days = 7) {
  return useApiQuery(['checkins', days], `/checkins?days=${days}`);
}

/**
 * Hook for fetching today's check-in
 */
export function useTodayCheckin() {
  return useApiQuery('checkin-today', '/checkins/today');
}

/**
 * Hook for logging a check-in
 */
export function useLogCheckin() {
  return useApiMutation(
    async (data) => {
      const response = await api.post('/checkins', data);
      return response.data;
    },
    {
      invalidateKeys: ['checkins', 'checkin-today', 'checkin-streak'],
    }
  );
}

/**
 * Hook for fetching check-in streak and weekly stats
 */
export function useCheckinStreak() {
  return useApiQuery('checkin-streak', '/checkins/streak');
}

/**
 * Hook for fetching team members
 */
export function useTeam() {
  return useApiQuery('team', '/users/me/team');
}

/**
 * Hook for inviting team members
 */
export function useInviteTeamMember() {
  return useApiMutation(
    async (data) => {
      const response = await api.post('/users/me/team', data);
      return response.data;
    },
    {
      invalidateKeys: ['team'],
    }
  );
}

/**
 * Hook for updating user profile
 */
export function useUpdateProfile() {
  return useApiMutation(
    async (data) => {
      const response = await api.put('/users/me', data);
      return response.data;
    },
    {
      invalidateKeys: ['user'],
    }
  );
}

/**
 * Hook for uploading avatar
 */
export function useUploadAvatar() {
  return useApiMutation(
    async (file) => {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await api.post('/users/me/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    {
      invalidateKeys: ['user'],
    }
  );
}

/**
 * Hook for changing password
 */
export function useChangePassword() {
  return useApiMutation(
    async ({ currentPassword, newPassword }) => {
      const response = await api.put('/users/me/password', {
        currentPassword,
        newPassword,
      });
      return response.data;
    }
  );
}

/**
 * Hook for saving a conversational onboarding screen
 */
export function useSaveOnboardingScreen() {
  return useApiMutation(
    async ({ screen, ...data }) => {
      const response = await api.put('/onboarding/conversational', { screen, ...data });
      return response.data;
    },
    {
      invalidateKeys: ['onboarding', 'user'],
    }
  );
}

/**
 * Hook for dismissing the onboarding card
 */
export function useDismissOnboarding() {
  return useApiMutation(
    async () => {
      const response = await api.post('/onboarding/dismiss');
      return response.data;
    },
    {
      invalidateKeys: ['onboarding', 'user'],
    }
  );
}

/**
 * Hook for fetching WHY statement history
 */
export function useWhyHistory() {
  return useApiQuery('why-history', '/users/me/why-history');
}

export default api;
